import { z } from 'zod'

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  remember_me: z.boolean().optional().default(false),
})

export const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters').optional(),
  role: z.string().min(1, 'Role is required'),
})

// User management form schema
export const createUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)'),
  employee_id: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  department: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  client_id: z.string().optional(),
  cluster: z.string().optional(),
  reports_to: z.string().optional(), // user_id of immediate supervisor (user_meta.reports_to)
  team_ids: z.array(z.number()).optional(), // team_members: which teams this user belongs to
  application_access: z.record(z.string(), z.object({ access: z.boolean(), auditor: z.boolean() })).optional(), // app code -> { access, auditor }
  user_status: z.number().int().min(0).max(2).default(1), // 0 = Inactive, 1 = Active, 2 = Suspended
})

export const updateUserSchema = z.object({
  user_login: z.string().min(1, 'Username is required'),
  user_email: z.string().email('Invalid email address'),
  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)')
    .optional()
    .or(z.literal('')),
  employee_id: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  department: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  client_id: z.string().optional(),
  cluster: z.string().optional(),
  reports_to: z.string().optional(), // user_id of immediate supervisor (user_meta.reports_to)
  team_ids: z.array(z.number()).optional(),
  application_access: z.record(z.string(), z.object({ access: z.boolean(), auditor: z.boolean() })).optional(),
  user_status: z.number().int().min(0).max(2).default(1), // 0 = Inactive, 1 = Active, 2 = Suspended
})

export type CreateUserFormData = z.infer<typeof createUserSchema>
export type UpdateUserFormData = z.infer<typeof updateUserSchema>


// Role management form schema
export const createRoleSchema = z.object({
  name: z.string().min(1, 'Role name is required').max(255, 'Role name must not exceed 255 characters'),
  active: z.boolean().default(true),
  permissions: z.record(
    z.string(), // parent navigation id
    z.record(
      z.string(), // navigation id
      z.record(
        z.string(), // permission name (can_view, can_create, etc.)
        z.boolean()
      )
    )
  ).refine(
    (permissions) => {
      // Check if at least one permission is set to true
      for (const parentNav in permissions) {
        for (const navId in permissions[parentNav]) {
          for (const permName in permissions[parentNav][navId]) {
            if (permissions[parentNav][navId][permName] === true) {
              return true
            }
          }
        }
      }
      return false
    },
    { message: 'Please select at least one permission' }
  ),
})

export const updateRoleSchema = createRoleSchema

export type CreateRoleFormData = z.infer<typeof createRoleSchema>
export type UpdateRoleFormData = z.infer<typeof updateRoleSchema>

// Navigation management form schema
export const navigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  path: z.string().min(1, 'Path is required'),
  icon: z.string().optional(),
  parent_id: z.number().optional(),
  permissions: z.array(z.string()).optional(),
})

export const createNavigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean().default(true),
  show_in_menu: z.boolean().default(true),
  application: z.enum(['auth', 'sima', 'support']).optional().nullable(),
})

export const updateNavigationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean().default(true),
  show_in_menu: z.boolean().default(true),
  application: z.enum(['auth', 'sima', 'support']).optional().nullable(),
})

export type CreateNavigationFormData = z.infer<typeof createNavigationSchema>
export type UpdateNavigationFormData = z.infer<typeof updateNavigationSchema>
export type NavigationFormData = z.infer<typeof navigationSchema>

// Email Template schemas
export const emailTemplateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.string().min(1, 'Type is required'),
  receiver: z.string().email('Invalid receiver email address').optional().or(z.literal('')),
  cc: z.string().optional(),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Body is required'),
  description: z.string().optional(),
  is_active: z.boolean().default(true),
})

export const createEmailTemplateSchema = emailTemplateSchema
export const updateEmailTemplateSchema = emailTemplateSchema

export type CreateEmailTemplateFormData = z.infer<typeof createEmailTemplateSchema>
export type UpdateEmailTemplateFormData = z.infer<typeof updateEmailTemplateSchema>
export type EmailTemplateFormData = z.infer<typeof emailTemplateSchema>

// Client management form schema
export const createClientSchema = z.object({
  code: z
    .string()
    .min(1, 'Client code is required')
    .max(255, 'Client code must not exceed 255 characters')
    .regex(/^[A-Z0-9_]+$/, 'Client code must only contain uppercase letters, numbers, and underscores'),
  name: z
    .string()
    .min(1, 'Client name is required')
    .max(255, 'Client name must not exceed 255 characters')
    .regex(/^[A-Za-z0-9\s\.\,\-\_\(\)\/\#\&]+$/, 'Client name contains invalid characters'),
  database_name: z
    .string()
    .min(1, 'Database name is required')
    .max(255, 'Database name must not exceed 255 characters')
    .regex(/^[a-z0-9_]+$/, 'Database name must only contain lowercase letters, numbers, and underscores'),
  status: z.boolean().default(true),
  config: z
    .object({
      timezone: z.string().max(255).optional(),
      currency: z.string().max(10).optional(),
      date_format: z.string().max(50).optional(),
    })
    .optional()
    .nullable(),
  description: z.string().max(1000).optional().nullable(),
})

export const updateClientSchema = createClientSchema

export type CreateClientFormData = z.infer<typeof createClientSchema>
export type UpdateClientFormData = z.infer<typeof updateClientSchema>

// Field Engineer management form schema
export const createFieldEngineerSchema = z.object({
  user_id: z.number().nullable().optional(),
  full_name: z.string().min(1, 'Full name is required').max(255, 'Full name must not exceed 255 characters'),
  cluster: z.string().min(1, 'Cluster is required').max(100, 'Cluster must not exceed 100 characters'),
  province: z.string().max(100, 'Province must not exceed 100 characters').optional().nullable(),
  region: z.string().max(100, 'Region must not exceed 100 characters').optional().nullable(),
  launch_point: z.string().min(1, 'Launch point is required').max(255, 'Launch point must not exceed 255 characters'),
  address: z.string().min(1, 'Address is required'),
  normalized_address: z.string().optional().nullable(),
  longitude: z.number().min(-180).max(180).optional().nullable(),
  latitude: z.number().min(-90).max(90).optional().nullable(),
  launch_longitude: z.number().min(-180).max(180).optional().nullable(),
  launch_latitude: z.number().min(-90).max(90).optional().nullable(),
  comments: z.string().optional().nullable(),
})

export const updateFieldEngineerSchema = createFieldEngineerSchema

export type CreateFieldEngineerFormData = z.infer<typeof createFieldEngineerSchema>
export type UpdateFieldEngineerFormData = z.infer<typeof updateFieldEngineerSchema>

// Field Engineer Certification form schema
export const createFieldEngineerCertificationSchema = z.object({
  certification_name: z.string().min(1, 'Certification name is required').max(255, 'Certification name must not exceed 255 characters'),
  certification_type: z.string().max(100, 'Certification type must not exceed 100 characters').optional().nullable(),
  issued_date: z.string().optional().nullable(),
  expiry_date: z.string().optional().nullable(),
  status: z.enum(['ACTIVE', 'EXPIRED', 'PENDING'], {
    required_error: 'Status is required',
  }),
  issuing_organization: z.string().max(255, 'Issuing organization must not exceed 255 characters').optional().nullable(),
  certificate_number: z.string().max(255, 'Certificate number must not exceed 255 characters').optional().nullable(),
  notes: z.string().optional().nullable(),
}).refine(
  (data) => {
    if (data.expiry_date && data.issued_date) {
      return new Date(data.expiry_date) >= new Date(data.issued_date)
    }
    return true
  },
  {
    message: 'Expiry date must be after or equal to issued date',
    path: ['expiry_date'],
  }
)

export const updateFieldEngineerCertificationSchema = createFieldEngineerCertificationSchema

export type CreateFieldEngineerCertificationFormData = z.infer<typeof createFieldEngineerCertificationSchema>
export type UpdateFieldEngineerCertificationFormData = z.infer<typeof updateFieldEngineerCertificationSchema>

// Assigned device form schema
export const createAssignedDeviceSchema = z.object({
  user_id: z.number({ required_error: 'User is required' }),
  model: z.string().min(1, 'Model is required').max(255),
  brand: z.string().min(1, 'Brand is required').max(255),
  serial_no: z.string().min(1, 'Serial number is required').max(255),
  os: z.string().max(255).optional().nullable(),
  os_version: z
    .any()
    .optional()
    .transform((v) => {
      if (v === '' || v == null || (typeof v === 'number' && Number.isNaN(v))) return undefined
      const n = Number(v)
      return Number.isNaN(n) ? undefined : n
    }),
  ram_size: z.string().max(255).optional().nullable(),
  storage_size: z.string().max(255).optional().nullable(),
  android_id: z.string().max(255).optional().nullable(),
  active: z.boolean(),
})

export const updateAssignedDeviceSchema = createAssignedDeviceSchema

export type CreateAssignedDeviceFormData = z.infer<typeof createAssignedDeviceSchema>
export type UpdateAssignedDeviceFormData = z.infer<typeof updateAssignedDeviceSchema>

export type LoginFormData = z.infer<typeof loginSchema>
export type UserFormData = z.infer<typeof userSchema>

